import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CustomFieldInput } from '../../components/CustomFieldInput';
import { Button, ErrorNote, Field, Modal, Tabs, Toggle, inputClass } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { formatDateTime, formatShortDate } from '../../lib/format';
import { GROUP_PALETTE } from '../../lib/palette';
import { customFieldGroups, customFields as customFieldsApi } from '../../lib/resources';
import type {
  CustomField,
  CustomFieldConfig,
  CustomFieldEntity,
  CustomFieldGroup,
  CustomFieldOption,
  CustomFieldType,
} from '../../types';

/** Os 6 cartões da aba "Tipo de dado". */
type Card = 'text' | 'number' | 'date' | 'boolean' | 'list' | 'rating';

const CARDS: Array<{ key: Card; label: string; hint: string }> = [
  { key: 'text', label: 'Texto', hint: 'Campo livre de texto' },
  { key: 'number', label: 'Número', hint: 'Inteiro, decimal, financeiro ou %' },
  { key: 'date', label: 'Data', hint: 'Data, hora ou data e hora' },
  { key: 'boolean', label: 'Booleano', hint: 'Sim / Não' },
  { key: 'list', label: 'Lista', hint: 'Opções customizadas' },
  { key: 'rating', label: 'Avaliação', hint: 'Estrelas (inteiro)' },
];

const cardOf = (type: CustomFieldType): Card => {
  switch (type) {
    case 'NUMBER':
      return 'number';
    case 'DATE':
      return 'date';
    case 'BOOLEAN':
      return 'boolean';
    case 'SELECT':
    case 'MULTI_SELECT':
      return 'list';
    case 'RATING':
      return 'rating';
    default:
      return 'text';
  }
};

const REGEX_PRESETS: Array<[string, string]> = [
  ['', 'Sem validação'],
  ['^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', 'E-mail'],
  ['^https?://\\S+$', 'URL'],
  ['^\\+?[\\d\\s()-]{8,20}$', 'Telefone'],
  ['^\\d{13,19}$', 'Cartão de crédito'],
];

const DATE_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD.MM.YYYY'];

const newOption = (index: number): CustomFieldOption => ({
  id: '',
  label: `Opção #${index + 1}`,
  color: GROUP_PALETTE[index % GROUP_PALETTE.length],
});

interface FormState {
  name: string;
  description: string;
  groupId: string | null;
  alsoCompany: boolean;
  isPublic: boolean;
  alwaysVisible: boolean;
  isIndexed: boolean;
  showOnProfile: boolean;
  card: Card;
  /** Tipo legado (PHONE/EMAIL/URL) preservado enquanto o cartão Texto não muda. */
  legacyType: CustomFieldType | null;
  multiple: boolean;
  options: CustomFieldOption[];
  config: CustomFieldConfig;
  helpText: string;
  validationRegex: string;
  validationMessage: string;
}

const initialState = (field: CustomField | null): FormState => ({
  name: field?.name ?? '',
  description: field?.description ?? '',
  groupId: field?.groupId ?? null,
  alsoCompany: field?.alsoCompany ?? false,
  isPublic: field?.isPublic ?? true,
  alwaysVisible: field?.alwaysVisible ?? false,
  isIndexed: field?.isIndexed ?? false,
  showOnProfile: field?.showOnProfile ?? false,
  card: field ? cardOf(field.type) : 'text',
  legacyType: field && ['PHONE', 'EMAIL', 'URL'].includes(field.type) ? field.type : null,
  multiple: field?.type === 'MULTI_SELECT',
  options: field?.options.length ? field.options : [newOption(0)],
  config: field?.config ?? {},
  helpText: field?.helpText ?? '',
  validationRegex: field?.validationRegex ?? '',
  validationMessage: field?.validationMessage ?? '',
});

const typeFor = (state: FormState): CustomFieldType => {
  switch (state.card) {
    case 'number':
      return 'NUMBER';
    case 'date':
      return 'DATE';
    case 'boolean':
      return 'BOOLEAN';
    case 'list':
      return state.multiple ? 'MULTI_SELECT' : 'SELECT';
    case 'rating':
      return 'RATING';
    default:
      if (state.legacyType) return state.legacyType;
      return (state.config.rows ?? 1) > 1 ? 'LONG_TEXT' : 'TEXT';
  }
};

const ENTITY_LABEL: Record<CustomFieldEntity, string> = {
  lead: 'Lead (Pessoa)',
  company: 'Lead (Empresa)',
  business: 'Negócio',
};

/** Novo / Editar campo adicional — 4 abas com pré-visualização (doc 01, 7). */
export function CustomFieldForm({
  entity,
  field,
  groups,
  defaultGroupId,
  onClose,
}: {
  entity: CustomFieldEntity;
  field: CustomField | null;
  groups: CustomFieldGroup[];
  defaultGroupId?: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'campo' | 'tipo' | 'avancado' | 'estatisticas'>('campo');
  const [state, setState] = useState<FormState>(() => ({
    ...initialState(field),
    groupId: field ? field.groupId : (defaultGroupId ?? null),
  }));
  const [preview, setPreview] = useState('');
  const [newGroupName, setNewGroupName] = useState<string | null>(null);

  const stats = useQuery({
    queryKey: ['custom-field-stats', field?.id],
    queryFn: () => customFieldsApi.stats(field!.id),
    enabled: Boolean(field),
  });
  // "O tipo não pode ser alterado porque o campo já tem valores preenchidos."
  const typeLocked = Boolean(field && (stats.data?.filled ?? 0) > 0);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((current) => ({ ...current, [key]: value }));
  const setConfig = (patch: CustomFieldConfig) =>
    setState((current) => ({ ...current, config: { ...current.config, ...patch } }));

  const createGroup = useMutation({
    mutationFn: (name: string) => customFieldGroups.create({ entity, name, columns: 1 }),
    onSuccess: (group) => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-groups'] });
      set('groupId', group.id);
      setNewGroupName(null);
    },
  });

  const save = useMutation({
    mutationFn: () => {
      const payload: Partial<CustomField> = {
        name: state.name.trim(),
        description: state.description.trim() || null,
        type: typeFor(state),
        entity,
        groupId: state.groupId,
        isPublic: state.isPublic,
        alwaysVisible: state.alwaysVisible,
        isIndexed: state.isIndexed,
        alsoCompany: entity === 'lead' ? state.alsoCompany : false,
        showOnProfile: entity === 'business' ? false : state.showOnProfile,
        options:
          state.card === 'list'
            ? state.options.map((option) => ({
                ...option,
                id: option.id || (undefined as unknown as string),
              }))
            : [],
        config: state.config,
        helpText: state.helpText.trim() || null,
        validationRegex: state.validationRegex.trim() || null,
        validationMessage: state.validationMessage.trim() || null,
      };
      return field
        ? customFieldsApi.update(field.id, payload)
        : customFieldsApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      onClose();
    },
  });

  const previewShape = {
    name: state.name || 'Nome do campo',
    type: typeFor(state),
    options: state.options.map((option, index) => ({
      ...option,
      id: option.id || `nova-${index}`,
    })),
    config: state.config,
    helpText: state.helpText,
  };

  const tabs: Array<[typeof tab, string]> = [
    ['campo', 'Campo'],
    ['tipo', 'Tipo de dado'],
    ['avancado', 'Avançado'],
  ];
  if (field) tabs.push(['estatisticas', 'Estatísticas']);

  return (
    <Modal
      title={`${field ? 'Editar' : 'Novo'} campo adicional — ${ENTITY_LABEL[entity]}`}
      onClose={onClose}
      wide
    >
      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      <div className="mt-4 grid gap-6 md:grid-cols-[1fr_15rem]">
        <div className="space-y-4">
          {tab === 'campo' && (
            <>
              <Field label="Nome *">
                <input
                  required
                  autoFocus
                  className={inputClass}
                  value={state.name}
                  onChange={(event) => set('name', event.target.value)}
                />
              </Field>
              <Field label="Descrição">
                <input
                  className={inputClass}
                  value={state.description}
                  onChange={(event) => set('description', event.target.value)}
                />
              </Field>
              <Field label="Grupo">
                {newGroupName === null ? (
                  <div className="flex gap-2">
                    <select
                      className={inputClass}
                      value={state.groupId ?? ''}
                      onChange={(event) => set('groupId', event.target.value || null)}
                    >
                      <option value="">Sem grupo</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                    <Button className="mt-1" onClick={() => setNewGroupName('')}>
                      Criar
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      autoFocus
                      className={inputClass}
                      placeholder="Nome do grupo"
                      value={newGroupName}
                      onChange={(event) => setNewGroupName(event.target.value)}
                    />
                    <Button
                      className="mt-1"
                      variant="primary"
                      disabled={!newGroupName.trim() || createGroup.isPending}
                      onClick={() => createGroup.mutate(newGroupName.trim())}
                    >
                      Salvar
                    </Button>
                    <Button className="mt-1" onClick={() => setNewGroupName(null)}>
                      Cancelar
                    </Button>
                  </div>
                )}
              </Field>
              {createGroup.error && <ErrorNote>{errorMessage(createGroup.error)}</ErrorNote>}

              <div className="space-y-3">
                {entity === 'lead' && (
                  <Toggle
                    label="Mostrar também em Lead Empresa"
                    checked={state.alsoCompany}
                    onChange={(checked) => set('alsoCompany', checked)}
                  />
                )}
                <Toggle
                  label="Público"
                  hint="Desligado, só administradores veem o campo."
                  checked={state.isPublic}
                  onChange={(checked) => set('isPublic', checked)}
                />
                <Toggle
                  label="Sempre visível"
                  checked={state.alwaysVisible}
                  onChange={(checked) => set('alwaysVisible', checked)}
                />
                <Toggle
                  label="Indexado"
                  hint="Indexa só valores de 8 a 40 caracteres (busca e deduplicação)."
                  checked={state.isIndexed}
                  onChange={(checked) => set('isIndexed', checked)}
                />
                {entity !== 'business' && (
                  <Toggle
                    label="Exibir no perfil"
                    hint="O campo aparece na seção Perfil da ficha do lead."
                    checked={state.showOnProfile}
                    onChange={(checked) => set('showOnProfile', checked)}
                  />
                )}
              </div>
            </>
          )}

          {tab === 'tipo' && (
            <>
              {typeLocked && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  O tipo não pode ser alterado porque o campo já tem valores preenchidos.
                </p>
              )}
              <div className="grid grid-cols-3 gap-2">
                {CARDS.map((card) => (
                  <button
                    key={card.key}
                    type="button"
                    disabled={typeLocked && card.key !== state.card}
                    onClick={() =>
                      setState((current) => ({
                        ...current,
                        card: card.key,
                        legacyType: card.key === 'text' ? current.legacyType : null,
                        config: card.key === current.card ? current.config : {},
                      }))
                    }
                    className={`rounded-lg border p-3 text-left text-sm disabled:opacity-40 ${
                      state.card === card.key
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <span className="block font-medium text-slate-800">{card.label}</span>
                    <span className="block text-xs text-slate-400">{card.hint}</span>
                  </button>
                ))}
              </div>
              <TypeConfig state={state} locked={typeLocked} set={set} setConfig={setConfig} />
            </>
          )}

          {tab === 'avancado' && (
            <>
              <Field label="Dica de ajuda" hint="Aparece como ⓘ ao lado do rótulo.">
                <input
                  className={inputClass}
                  value={state.helpText}
                  onChange={(event) => set('helpText', event.target.value)}
                />
              </Field>
              <Field label="Regex de validação">
                <select
                  className={inputClass}
                  value={
                    REGEX_PRESETS.some(([regex]) => regex === state.validationRegex)
                      ? state.validationRegex
                      : 'custom'
                  }
                  onChange={(event) =>
                    set(
                      'validationRegex',
                      event.target.value === 'custom' ? '^$' : event.target.value,
                    )
                  }
                >
                  {REGEX_PRESETS.map(([regex, label]) => (
                    <option key={label} value={regex}>
                      {label}
                    </option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
              </Field>
              {!REGEX_PRESETS.some(([regex]) => regex === state.validationRegex) && (
                <Field label="Expressão (custom)">
                  <input
                    className={`${inputClass} font-mono`}
                    value={state.validationRegex}
                    onChange={(event) => set('validationRegex', event.target.value)}
                  />
                </Field>
              )}
              <Field label="Mensagem de erro de validação">
                <input
                  className={inputClass}
                  value={state.validationMessage}
                  onChange={(event) => set('validationMessage', event.target.value)}
                />
              </Field>
            </>
          )}

          {tab === 'estatisticas' && field && (
            <StatsPanel loading={stats.isLoading} data={stats.data} />
          )}
        </div>

        <aside className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Pré-visualização
          </p>
          <label className="mt-3 block text-sm">
            <span className="font-medium text-slate-700">
              {previewShape.name}
              {state.helpText && (
                <span className="ml-1 cursor-help text-slate-400" title={state.helpText}>
                  ⓘ
                </span>
              )}
            </span>
            <CustomFieldInput field={previewShape} value={preview} onChange={setPreview} />
          </label>
        </aside>
      </div>

      {save.error && (
        <div className="mt-4">
          <ErrorNote>{errorMessage(save.error, 'Não foi possível salvar o campo')}</ErrorNote>
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          disabled={!state.name.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Salvando…' : 'Salvar campo'}
        </Button>
      </div>
    </Modal>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
}) {
  return (
    <Field label={label}>
      <input
        type="number"
        min={min}
        max={max}
        className={inputClass}
        value={value ?? ''}
        onChange={(event) =>
          onChange(event.target.value === '' ? undefined : Number(event.target.value))
        }
      />
    </Field>
  );
}

function TypeConfig({
  state,
  locked,
  set,
  setConfig,
}: {
  state: FormState;
  locked: boolean;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  setConfig: (patch: CustomFieldConfig) => void;
}) {
  const { config } = state;

  if (state.card === 'text') {
    const rows = config.rows ?? 1;
    return (
      <div className="grid gap-3 md:grid-cols-2">
        {state.legacyType && (
          <p className="md:col-span-2 text-xs text-slate-500">
            Campo legado do tipo {state.legacyType}; a validação desse tipo continua valendo.
          </p>
        )}
        <Field label="Formato">
          <select
            className={inputClass}
            value={config.format ?? 'FREE'}
            onChange={(event) =>
              setConfig({ format: event.target.value as CustomFieldConfig['format'] })
            }
          >
            <option value="FREE">Livre</option>
            <option value="CPF">CPF</option>
            <option value="CNPJ">CNPJ</option>
            <option value="CEP">CEP</option>
            <option value="MOBILE">Celular</option>
            <option value="LANDLINE">Fixo</option>
            <option value="CUSTOM">Customizada</option>
          </select>
        </Field>
        {config.format === 'CUSTOM' ? (
          <Field label="Máscara">
            <input
              className={inputClass}
              placeholder="AAA-9999"
              value={config.mask ?? ''}
              onChange={(event) => setConfig({ mask: event.target.value })}
            />
          </Field>
        ) : (
          <div />
        )}
        <NumberInput
          label="Mínimo de caracteres"
          min={0}
          value={config.minLength ?? 0}
          onChange={(minLength) => setConfig({ minLength })}
        />
        <NumberInput
          label="Máximo de caracteres"
          min={1}
          value={config.maxLength ?? 512}
          onChange={(maxLength) => setConfig({ maxLength })}
        />
        <Field label="Altura (linhas)">
          <div className="mt-1 flex items-center gap-2">
            <Button
              disabled={locked || rows <= 1}
              onClick={() => setConfig({ rows: Math.max(1, rows - 1) })}
            >
              −
            </Button>
            <span className="w-6 text-center text-sm">{rows}</span>
            <Button
              disabled={locked || rows >= 20}
              onClick={() => setConfig({ rows: rows + 1 })}
            >
              +
            </Button>
          </div>
        </Field>
      </div>
    );
  }

  if (state.card === 'number') {
    return (
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Subtipo">
          <select
            disabled={locked}
            className={inputClass}
            value={config.subtype ?? 'INTEGER'}
            onChange={(event) =>
              setConfig({ subtype: event.target.value as CustomFieldConfig['subtype'] })
            }
          >
            <option value="INTEGER">Inteiro</option>
            <option value="DECIMAL">Decimal</option>
            <option value="PERCENT">Porcentagem</option>
            <option value="CURRENCY">Financeiro (centavos)</option>
          </select>
        </Field>
        <NumberInput
          label="Valor mínimo"
          value={config.min}
          onChange={(min) => setConfig({ min })}
        />
        <NumberInput
          label="Valor máximo"
          value={config.max}
          onChange={(max) => setConfig({ max })}
        />
      </div>
    );
  }

  if (state.card === 'date') {
    const custom = config.dateFormat !== undefined && !DATE_FORMATS.includes(config.dateFormat);
    return (
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Subtipo">
          <select
            disabled={locked}
            className={inputClass}
            value={config.subtype ?? 'DATE'}
            onChange={(event) =>
              setConfig({ subtype: event.target.value as CustomFieldConfig['subtype'] })
            }
          >
            <option value="DATE">Data</option>
            <option value="TIME">Hora</option>
            <option value="DATETIME">Data e hora</option>
          </select>
        </Field>
        <Field label="Formato de exibição">
          <select
            className={inputClass}
            value={custom ? 'custom' : (config.dateFormat ?? 'DD/MM/YYYY')}
            onChange={(event) =>
              setConfig({
                dateFormat: event.target.value === 'custom' ? 'DD/MM' : event.target.value,
              })
            }
          >
            {DATE_FORMATS.map((format) => (
              <option key={format} value={format}>
                {format}
              </option>
            ))}
            <option value="custom">Custom</option>
          </select>
        </Field>
        {custom && (
          <Field label="Máscara de data">
            <input
              className={inputClass}
              value={config.dateFormat ?? ''}
              onChange={(event) => setConfig({ dateFormat: event.target.value })}
            />
          </Field>
        )}
      </div>
    );
  }

  if (state.card === 'boolean') {
    return (
      <p className="text-sm text-slate-500">
        Este tipo de campo não possui validações configuráveis.
      </p>
    );
  }

  if (state.card === 'rating') {
    const max = config.maxStars ?? 5;
    return (
      <Field
        label={`Máximo de estrelas: ${max}`}
        hint="Sempre um valor inteiro, de 1 até o máximo definido."
      >
        <input
          type="range"
          min={1}
          max={10}
          className="mt-2 w-full"
          value={max}
          onChange={(event) => setConfig({ maxStars: Number(event.target.value) })}
        />
      </Field>
    );
  }

  // Lista
  return (
    <div className="space-y-3">
      <Field label="Modo de seleção">
        <select
          disabled={locked}
          className={inputClass}
          value={state.multiple ? 'multiple' : 'single'}
          onChange={(event) => set('multiple', event.target.value === 'multiple')}
        >
          <option value="single">Escolha única</option>
          <option value="multiple">Múltipla escolha</option>
        </select>
      </Field>
      <div>
        <p className="text-sm font-medium text-slate-700">Configuração de opções</p>
        <ul className="mt-2 space-y-2">
          {state.options.map((option, index) => (
            <li key={option.id || `nova-${index}`} className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Trocar cor"
                className="h-5 w-5 shrink-0 rounded-full border border-slate-200"
                style={{ backgroundColor: option.color ?? '#94a3b8' }}
                onClick={() => {
                  const next =
                    GROUP_PALETTE[
                      (GROUP_PALETTE.indexOf(option.color ?? '') + 1) % GROUP_PALETTE.length
                    ];
                  set(
                    'options',
                    state.options.map((item, position) =>
                      position === index ? { ...item, color: next } : item,
                    ),
                  );
                }}
              />
              <input
                className={`${inputClass} mt-0`}
                value={option.label}
                onChange={(event) =>
                  set(
                    'options',
                    state.options.map((item, position) =>
                      position === index ? { ...item, label: event.target.value } : item,
                    ),
                  )
                }
              />
              <button
                type="button"
                aria-label={`Remover ${option.label}`}
                disabled={state.options.length === 1}
                className="text-xs text-slate-400 hover:text-red-600 disabled:opacity-30"
                onClick={() =>
                  set(
                    'options',
                    state.options.filter((_, position) => position !== index),
                  )
                }
              >
                Remover
              </button>
            </li>
          ))}
        </ul>
        <Button
          className="mt-2"
          onClick={() => set('options', [...state.options, newOption(state.options.length)])}
        >
          + Adicionar opção
        </Button>
      </div>
    </div>
  );
}

function StatsPanel({
  loading,
  data,
}: {
  loading: boolean;
  data?: Awaited<ReturnType<typeof customFieldsApi.stats>>;
}) {
  if (loading || !data) return <p className="text-sm text-slate-400">Carregando…</p>;
  const kb = (data.estimatedBytes / 1024).toFixed(1);
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Registros preenchidos" value={String(data.filled)} />
        <Stat label="Espaço em disco (estimado)" value={`${kb} KB`} />
        <Stat
          label="Última vez usado"
          value={data.lastUsedAt ? formatDateTime(data.lastUsedAt) : '—'}
        />
      </div>
      <div>
        <p className="text-xs text-slate-500">
          Taxa de preenchimento: <strong>{data.fillRate}%</strong> (de {data.total} no total)
        </p>
        <div className="mt-1 h-2 rounded-full bg-slate-100">
          <div
            className="h-2 rounded-full"
            style={{ width: `${data.fillRate}%`, backgroundColor: 'var(--crm-primary)' }}
          />
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
        <dt>Criado em</dt>
        <dd>{formatShortDate(data.createdAt)}</dd>
        <dt>Última edição da configuração</dt>
        <dd>{formatDateTime(data.configUpdatedAt)}</dd>
        <dt>Tamanho médio do valor</dt>
        <dd>{data.averageSize} bytes</dd>
        <dt>Registros vazios</dt>
        <dd>
          {data.empty} ({data.emptyRate}%)
        </dd>
      </dl>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className="text-[11px] text-slate-400">{label}</p>
      <p className="mt-1 text-base font-semibold text-slate-800">{value}</p>
    </div>
  );
}
